export const vocalsWesternClassicalSyllabus = {
  1: {
    id: 1,
    title: "Level 1 — Initial Basic (Foundation Level)",
    objective:
      "To establish healthy classical vocal technique, correct posture and breathing, basic pitch accuracy, tonal awareness, rhythmic discipline and fundamental musical understanding.",
    practicalSections: [
      {
        title: "Vocal & body fundamentals",
        items: [
          "Understanding the voice as an instrument",
          "Correct singing posture (standing alignment)",
          "Relaxed shoulders and jaw",
          "Body relaxation and tension release",
          "Awareness of natural breath",
        ],
      },
      {
        title: "Breathing technique",
        items: [
          "Introduction to diaphragmatic breathing",
          "Silent inhalation and controlled exhalation",
          "Breath–voice coordination",
        ],
      },
      {
        title: "Tone production",
        items: [
          "Natural vocal onset without forcing",
          "Open throat awareness",
          "Basic resonance awareness",
          "Even tone production across a limited range",
        ],
      },
      {
        title: "Pitch & exercises",
        items: [
          "Accurate pitch matching within a comfortable range",
          "Ascending and descending pitch patterns",
          "Major scale and natural minor scale patterns (comfortable keys)",
          "Five-note and simple arpeggio exercises",
          "Legato vocalises and sustained vowel exercises",
        ],
      },
      {
        title: "Diction & rhythm",
        items: [
          "Introduction to pure vowels (Ah, Eh, Ee, Oh, Oo)",
          "Simple consonant clarity",
          "Singing with steady pulse and basic note values",
        ],
      },
      {
        title: "Repertoire",
        items: [
          "Two pieces: one simple classical song or hymn; one vocalise or study",
          "Songs may be in English, Latin or simple Italian text",
          "Focus on tone, breath and clarity",
        ],
      },
    ],
    theorySections: [
      {
        title: "Music fundamentals",
        items: [
          "What is music; sound and voice production",
          "Pitch and duration; singing vs speaking voice",
        ],
      },
      {
        title: "Notes, rhythm & scales",
        items: [
          "Musical alphabet (A–G)",
          "Whole, half and quarter notes and rests",
          "Concept of scales and major vs minor sound (aural)",
        ],
      },
      {
        title: "Vocal health & notation (introductory)",
        items: [
          "Basic vocal hygiene, hydration and rest",
          "Safe practice habits",
          "Optional staff awareness: note direction, bar lines, simple time signatures",
        ],
      },
    ],
  },
  2: {
    id: 2,
    title: "Level 2 — Basic Development (Breath Control & Tonal Consistency)",
    objective:
      "To develop controlled breathing, improved tonal consistency, expanded vocal range awareness, clearer diction and confident performance of simple Western classical repertoire.",
    practicalSections: [
      {
        title: "Vocal & body technique",
        items: [
          "Reinforcement of posture and alignment",
          "Reduction of unnecessary tension",
          "Awareness of balanced support while singing",
        ],
      },
      {
        title: "Breathing & tone",
        items: [
          "Deepened diaphragmatic breathing",
          "Sustained exhalation for longer phrases",
          "Breath management for legato lines",
          "Even tone across low and middle registers",
        ],
      },
      {
        title: "Pitch, exercises & diction",
        items: [
          "Improved pitch matching and stepwise patterns",
          "Major scales in comfortable keys",
          "Simple arpeggios (tonic triads)",
          "Introduction to Italian diction basics and pure vowel uniformity",
        ],
      },
      {
        title: "Rhythm & repertoire",
        items: [
          "Half, quarter and eighth-note patterns and awareness of strong/weak beats",
          "2–3 pieces including one simple art song / hymn, one Italian song or aria excerpt and one vocalise",
        ],
      },
    ],
    theorySections: [
      {
        title: "Rhythm, meter & scales",
        items: [
          "Review of basic note values and rests",
          "Introduction to eighth notes and rests",
          "Concept of major and natural minor scales",
        ],
      },
      {
        title: "Harmony & health",
        items: [
          "Melody vs harmony (concept)",
          "Role of accompaniment in classical singing",
          "Safe warm-up and cool-down routines",
        ],
      },
    ],
  },
  3: {
    id: 3,
    title: "Level 3 — Intermediate (Range Expansion & Expressive Control)",
    objective:
      "To achieve expanded vocal range, improved resonance balance, expressive dynamic control, secure intonation and confident performance of intermediate Western classical repertoire.",
    practicalSections: [
      {
        title: "Vocal & body technique",
        items: [
          "Consistent posture with greater physical freedom",
          "Awareness of body support for longer singing",
          "Release of habitual tension",
        ],
      },
      {
        title: "Breathing, tone & exercises",
        items: [
          "Sustained breath control for longer phrases",
          "Balanced resonance between chest and head registers",
          "Smooth register transitions (passaggio awareness, conceptual)",
          "Major and natural/harmonic minor scales across expanded range",
          "Arpeggios (tonic and dominant)",
          "Chromatic scale in limited range",
        ],
      },
      {
        title: "Diction, rhythm & repertoire",
        items: [
          "Italian diction refinement and introduction to Latin or German sounds",
          "Dotted rhythms, simple syncopation and basic compound time awareness",
          "3 pieces including one classical art song, one Italian aria or art song and one technical vocalise",
        ],
      },
    ],
    theorySections: [
      {
        title: "Scales, keys & harmony",
        items: [
          "Major and minor key relationships (relative keys)",
          "Key signatures up to 4 sharps/flats",
          "Concept of triads and cadences (basic)",
        ],
      },
      {
        title: "Form, health & notation",
        items: [
          "Binary and ternary forms and phrase structure",
          "Managing vocal load, recognising strain vs healthy effort",
          "Expanded melodic reading and dynamic/articulation markings",
        ],
      },
    ],
  },
  4: {
    id: 4,
    title: "Level 4 — Advanced 1 (Technical Refinement & Stylistic Maturity)",
    objective:
      "To develop advanced technical fluency, refined resonance balance, consistent passaggio control, expressive nuance and stylistically mature performance of advanced Western classical repertoire.",
    practicalSections: [
      {
        title: "Technique, breathing & tone",
        items: [
          "Efficient posture for extended performance",
          "Advanced breath management for long phrases",
          "Unified vocal registers with controlled passaggio",
          "Rich, resonant tone and tone-colour variation",
        ],
      },
      {
        title: "Pitch, diction, rhythm & repertoire",
        items: [
          "Secure intonation in wide-ranging melodies and chromatic passages",
          "Italian diction mastery at intermediate level and introduction to German or French",
          "Compound meters (6/8, 9/8), syncopation and rubato timing",
          "4 pieces including one advanced art song, one operatic aria/excerpt, one contrasting style piece and one technical vocalise",
        ],
      },
    ],
    theorySections: [
      {
        title: "Harmony, form & practice",
        items: [
          "Concept of secondary dominants and modulation awareness",
          "Rondo and through-composed forms and thematic development",
          "Stage presence, classical performance etiquette and working with accompanists",
        ],
      },
      {
        title: "Vocal health & notation",
        items: [
          "Injury prevention and sustainable practice routines",
          "Advanced score reading, expressive markings and Da capo / dal segno / coda symbols",
        ],
      },
    ],
  },
  5: {
    id: 5,
    title: "Level 5 — Professional (Artistic Refinement & Performance Authority)",
    objective:
      "To certify professional-level vocal technique, artistic individuality, stylistic authority and confident performance of demanding Western classical and operatic repertoire.",
    practicalSections: [
      {
        title: "Technique, tone & breathing",
        items: [
          "Complete physical freedom and endurance for extended repertoire",
          "Mature, resonant classical tone with controlled vibrato",
          "Advanced breath control for operatic phrasing",
        ],
      },
      {
        title: "Pitch, diction, rhythm & repertoire",
        items: [
          "Absolute intonation accuracy across full professional range",
          "Italian diction mastery and proficiency in German and French",
          "Complex meters and tempo changes with rubato and expressive freedom",
          "5 pieces including one full operatic aria, one advanced art song, one contrasting style/language piece, one ensemble/duet and one advanced vocalise",
        ],
      },
    ],
    theorySections: [
      {
        title: "Advanced harmony, history & practice",
        items: [
          "Functional and chromatic harmony and phrase/form analysis",
          "Overview of Western vocal music periods and stylistic differences",
          "Professional rehearsal techniques, score preparation and vocal workload management",
        ],
      },
    ],
  },
  6: {
    id: 6,
    title: "Level 6 — Mastery (Artist / Educator Level)",
    objective:
      "To certify complete technical freedom, artistic authority, pedagogical awareness and leadership-level mastery in Western classical and operatic vocal performance.",
    practicalSections: [
      {
        title: "Performance & artistry",
        items: [
          "45–60 minute recital including multi-language and multi-style repertoire",
          "Advanced operatic and art song literature with clear artistic identity",
        ],
      },
      {
        title: "Technical mastery & interpretation",
        items: [
          "Effortless control across entire vocal range with advanced resonance and vibrato control",
          "Deep textual and musical interpretation with stylistic awareness",
        ],
      },
    ],
    theorySections: [
      {
        title: "Advanced studies, pedagogy & professional practice",
        items: [
          "In-depth harmonic and formal analysis and historical performance practice",
          "Vocal teaching principles, voice classification and diagnosis of technical problems",
          "Curriculum and syllabus planning and research/lecture-recital or thesis-style work",
          "Audition preparation, repertoire planning, career management and artistic leadership",
        ],
      },
    ],
  },
};
